import { i18next } from '@multaqa/i18n'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addCustomerContact,
  attachCustomerTag,
  type CustomerCreatePayload,
  type CustomerIndexParams,
  type CustomerUpdatePayload,
  createCustomer,
  deactivateCustomer,
  detachCustomerTag,
  fetchCustomer,
  fetchCustomerFinancialSummary,
  fetchCustomers,
  fetchTags,
  removeCustomerContact,
  type StoreContactRequest,
  updateCustomer,
} from '../api'

export const customerKeys = {
  all: ['customers'] as const,
  lists: () => [...customerKeys.all, 'list'] as const,
  list: (params?: CustomerIndexParams) => [...customerKeys.lists(), params] as const,
  details: () => [...customerKeys.all, 'detail'] as const,
  detail: (id: string) => [...customerKeys.details(), id] as const,
  tags: ['tags'] as const,
}

export function useCustomers(params?: CustomerIndexParams, enabled = true) {
  return useQuery({
    queryKey: customerKeys.list(params),
    queryFn: () => fetchCustomers(params),
    enabled,
  })
}

export function useCustomer(id: string) {
  return useQuery({
    queryKey: customerKeys.detail(id),
    queryFn: () => fetchCustomer(id),
    enabled: Boolean(id),
  })
}

export function useCustomerFinancialSummary(id: string) {
  return useQuery({
    queryKey: [...customerKeys.detail(id), 'financial-summary'],
    queryFn: () => fetchCustomerFinancialSummary(id),
    enabled: Boolean(id),
  })
}

export function useCreateCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CustomerCreatePayload) => createCustomer(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.all })
      toast.success(i18next.t('customer.createSuccess'))
    },
  })
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CustomerUpdatePayload }) =>
      updateCustomer(id, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.all })
      void queryClient.invalidateQueries({ queryKey: customerKeys.detail(variables.id) })
      toast.success(i18next.t('customer.updateSuccess'))
    },
  })
}

export function useDeactivateCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deactivateCustomer(id),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.all })
      void queryClient.invalidateQueries({ queryKey: customerKeys.detail(id) })
      toast.success(i18next.t('customer.deactivateSuccess'))
    },
  })
}

/**
 * Bring a deactivated record back into use.
 *
 * There is no `activate` endpoint — deactivation is a POST, reactivation is
 * the ordinary update with `is_active`. Without this the state was one-way
 * from the UI: the only route back was Edit, and a checkbox at the bottom of
 * a long form is not where anyone looks for it.
 */
export function useReactivateCustomer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => updateCustomer(id, { is_active: true }),
    onSuccess: (_, id) => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.all })
      void queryClient.invalidateQueries({ queryKey: customerKeys.detail(id) })
      toast.success(i18next.t('customer.activateSuccess'))
    },
  })
}

export function useAddContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ customerId, data }: { customerId: string; data: StoreContactRequest }) =>
      addCustomerContact(customerId, data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.detail(variables.customerId) })
      toast.success(i18next.t('customer.contactAdded'))
    },
  })
}

export function useRemoveContact() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ customerId, contactId }: { customerId: string; contactId: string }) =>
      removeCustomerContact(customerId, contactId),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.detail(variables.customerId) })
      toast.success(i18next.t('customer.contactRemoved'))
    },
  })
}

export function useAttachTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ customerId, tagId }: { customerId: string; tagId: string }) =>
      attachCustomerTag(customerId, tagId),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.detail(variables.customerId) })
      toast.success(i18next.t('customer.tagAttached'))
    },
  })
}

export function useDetachTag() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ customerId, tagId }: { customerId: string; tagId: string }) =>
      detachCustomerTag(customerId, tagId),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.detail(variables.customerId) })
      toast.success(i18next.t('customer.tagDetached'))
    },
  })
}

export function useTags() {
  return useQuery({
    queryKey: customerKeys.tags,
    queryFn: () => fetchTags(),
  })
}
